import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { onboardingApi } from "@/redux/apis/onboarding/onboardingApi";
import { baseQuery } from "@/redux/baseQuery";
import { Operator } from "@/redux/slices/operator/types";
import { transformOperator } from "./helpers";
import {
  AddAccreditationRawResponse,
  AuthenticationResponse,
  Credentials,
  RegisterClientParams,
  RegisterDriverParams,
  RegisterOperatorParams,
  RegisterOperatorRawResponse,
  RegisterProviderClientParams,
  RegisterUserParams,
  RegisterUserAccountParams,
  VerifyProviderParams,
  VerifyProviderTokenRawResponse,
  VerifyEmailAddressRawResponse,
  RegisterProviderDriverParams,
  RegisterProviderWithKindeParams,
} from "./types";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery,
  endpoints: (builder) => ({
    /**
     * @deprecated This endpoint is deprecated with Kinde migration.
     * Still used by: SignUpFormClient, SignUpFormOperator, SignUpFormInvite
     * TODO: Migrate these forms to use Kinde authentication flow
     */
    login: builder.mutation<AuthenticationResponse, Credentials>({
      query: (credentials: Credentials) => {
        credentials.grant_type = "password";
        credentials.scope = "web-app";
        credentials.client_id = "web-app";

        return {
          url: "/oauth",
          method: "POST",
          body: credentials,
        };
      },
      invalidatesTags: ["Auth"],
    }),
    verifyProvider: builder.query<Operator, VerifyProviderParams>({
      query: ({ providerCode, forDriver, apiUrl }) => ({
        url: `${apiUrl}/v3/auth/verify/provider`,
        method: "POST",
        body: { provider_code: providerCode, ...(forDriver && { verify_for: "driver" }) },
      }),
      transformResponse: transformOperator,
    }),
    verifyProviderToken: builder.query<VerifyProviderTokenRawResponse, string>({
      query: (token) => ({
        url: "/v3/auth/verify/provider-invitation-token",
        method: "POST",
        body: { token },
      }),
    }),
    registerProviderClient: builder.mutation<void, RegisterProviderClientParams>({
      query: ({ providerId, ...body }) => ({
        url: `/v3/auth/register/${providerId}/client`,
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(onboardingApi.endpoints.getOnboardingStatus.initiate(undefined, { forceRefetch: true }));
        } catch {
          console.error("Failed to register provider client");
        }
      },
    }),
    registerClient: builder.mutation<void, RegisterClientParams>({
      query: ({ apiUrl, ...body }) => ({
        url: `${apiUrl}/v3/auth/register/client`,
        method: "POST",
        body,
      }),
    }),
    registerProviderDriver: builder.mutation<void, RegisterProviderDriverParams & { providerId: string }>({
      query: ({ providerId, ...body }) => ({
        url: `/v3/auth/register/${providerId}/driver`,
        method: "POST",
        body,
      }),
    }),
    registerDriver: builder.mutation<void, RegisterDriverParams>({
      query: ({ apiUrl, ...body }) => ({
        url: `${apiUrl}/v3/auth/register/driver`,
        method: "POST",
        body,
      }),
    }),
    addAccreditation: builder.mutation<AddAccreditationRawResponse, FormData>({
      query: (body) => ({
        url: "/v3/auth/register/provider/accreditation",
        method: "POST",
        body,
      }),
    }),
    registerOperator: builder.mutation<RegisterOperatorRawResponse, RegisterOperatorParams>({
      query: (body) => ({
        url: "/v3/auth/register/provider",
        method: "POST",
        body,
      }),
    }),
    registerProviderWithKinde: builder.mutation<RegisterOperatorRawResponse, RegisterProviderWithKindeParams>({
      query: (body) => ({
        url: "/v3/auth/register/provider",
        method: "POST",
        body,
      }),
    }),
    getProvider: builder.query<Operator, string>({
      query: (providerId) => ({
        url: `/v3/provider/${providerId}`,
        method: "GET",
      }),
      transformResponse: transformOperator,
    }),
    registerUser: builder.mutation<void, RegisterUserParams>({
      query: ({ apiUrl, inviteToken, ...body }) => ({
        url: `${apiUrl}/v3/auth/register/user/${inviteToken}`,
        method: "POST",
        body,
      }),
    }),
    verifyEmailAddress: builder.mutation<VerifyEmailAddressRawResponse, string>({
      query: (emailAddress) => ({
        url: "/v3/auth/verify/email-address",
        method: "POST",
        body: { email_address: emailAddress },
      }),
    }),
    /**
     * Register a user account via Kinde flow (for invited team members)
     * POST /v3/auth/register/user/{inviteKey}
     */
    registerUserAccount: builder.mutation<void, RegisterUserAccountParams>({
      query: ({ apiUrl, inviteKey, mobile }) => ({
        url: `${apiUrl}/v3/auth/register/user/${inviteKey}`,
        method: "POST",
        body: mobile ? { mobile } : {},
      }),
    }),
    /**
     * Request password reset via Kinde Management API
     * This triggers Kinde's password reset flow by setting is_password_reset_requested flag
     * and invalidating all user sessions
     * POST /v1/auth/password-reset/{accountId}/request
     */
    requestPasswordReset: builder.mutation<{ success: boolean }, string>({
      query: (accountId: string) => ({
        url: `/v1/auth/password-reset/${accountId}/request`,
        method: "POST",
      }),
      invalidatesTags: ["Auth"],
    }),
    /**
     * Provider booking config by provider code (mobile parity: getProviderV1).
     * GET /v1/provider/{providerCode} — exposes settings.max_waypoints.
     *
     * @deprecated Not a wrong VALUE — it reads the same `ff-booking-config-max-stops` flag as
     * job/data — but a worse SOURCE: it is keyed by `activeOperator.providerCode`, so it silently
     * yields 0 (no "Add Stop") wherever that isn't loaded, it can't be authenticated in the guest
     * flow, and it is a second request for a number job/data already carries. Read `max_waypoints`
     * off `useBookingData()` instead. No caller left in the client flow.
     */
    getProviderConfig: builder.query<{ maxWaypoints: number }, string>({
      query: (providerCode: string) => ({ url: `/v1/provider/${providerCode}`, method: "GET" }),
      transformResponse: (raw: { settings?: { max_waypoints?: number } }) => ({
        maxWaypoints: raw?.settings?.max_waypoints ?? 0,
      }),
    }),
  }),
  tagTypes: ["Auth"],
});

export const {
  /** @deprecated - Use Kinde authentication instead */
  useLoginMutation,
  useLazyVerifyProviderQuery,
  useVerifyProviderTokenQuery,
  useRegisterClientMutation,
  useRegisterProviderClientMutation,
  useRegisterDriverMutation,
  useRegisterProviderDriverMutation,
  useAddAccreditationMutation,
  useRegisterOperatorMutation,
  useRegisterProviderWithKindeMutation,
  useLazyGetProviderQuery,
  useRegisterUserMutation,
  useRegisterUserAccountMutation,
  useVerifyEmailAddressMutation,
  useRequestPasswordResetMutation,
  /** @deprecated Stop cap comes from `useBookingData().max_waypoints` — see getProviderConfig. */
  useGetProviderConfigQuery,
} = authApi;
